import { Divider, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { PlusCircle } from 'react-feather';
import { TFunction, useTranslation } from 'react-i18next';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { InspectionActivity, Plan } from '../../../../types/plan';
import { IconButton } from '../../../global';
import { CheckInput } from '../../../global/CheckInput';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.InspectionTasks';
/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${focused ? '' : '-small'}`),
    subtitle: t(`${mod}.header-subtitle${focused ? '' : '-small'}`, {
        count: 1,
    }),
});

const useComponentClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '-7.5px 0',
        '& > div': {
            margin: '7.5px 0',
        },
    },
});

/**
 * Inspection plan activity editor of the plan creation workflow.
 *
 * @param props.state state of the plan in the inspection plan creation flow
 * @param props.next received function to proceed to next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useComponentClasses();
    const dispatch = useTypedDispatch();
    const { plan } = useTypedSelector(state => ({ plan: state.plan.changed }));

    const addTask = (activity: InspectionActivity, method: string) => {
        if (!plan) return;
        const categoryCopy: InspectionActivity = JSON.parse(JSON.stringify(activity));
        (method !== '' ? categoryCopy.methods[method] : categoryCopy.preparation_steps).push('');
        const activities = [...(plan.inspection_activity ?? []).filter(a => a.id !== activity.id), categoryCopy];
        dispatch(actions.plan.edit({ inspection_activity: activities }));
    };

    const editTask = (activity: InspectionActivity, method: string, task: string, index: number) => {
        if (!plan) return;
        const categoryCopy: InspectionActivity = JSON.parse(JSON.stringify(activity));
        (method !== '' ? categoryCopy.methods[method] : categoryCopy.preparation_steps).splice(index, 1, task);
        const activities = [...(plan.inspection_activity ?? []).filter(a => a.id !== activity.id), categoryCopy];
        dispatch(actions.plan.edit({ inspection_activity: activities }));
    };

    const deleteStep = (activity: InspectionActivity, method: string, index: number) => {
        if (!plan) return;
        const categoryCopy: InspectionActivity = JSON.parse(JSON.stringify(activity));
        (method !== '' ? categoryCopy.methods[method] : categoryCopy.preparation_steps).splice(index, 1);
        const activities = [...(plan.inspection_activity ?? []).filter(a => a.id !== activity.id), categoryCopy];
        dispatch(actions.plan.edit({ inspection_activity: activities }));
    };

    if (!plan) return <span>{'#plan not in store'}</span>;

    return (
        <div className={classes.root}>
            {[...(plan?.inspection_activity ?? [])]
                .filter(activity => activity.required)
                .sort((a, b) => -a.type.localeCompare(b.type))
                .map(activity => (
                    <Activity
                        key={activity.type}
                        activity={activity}
                        editable={props.state.editable}
                        addTask={addTask}
                        editTask={editTask}
                        deleteTask={deleteStep}
                    />
                ))}
        </div>
    );
};

const useInspectionActivityClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 10px',
        borderRadius: 5,
        '&.internal': {
            backgroundColor: theme.colors.green[5],
        },
        '&.external': {
            backgroundColor: theme.colors.amber[4],
        },
        '& > span': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h2,
            marginBottom: 0,
        },
    },
});

/**
 * Component to show inspection activity details.
 *
 * @param props.activity activity to describe
 * @param props.uncolored indicates if background is uncolored
 * @param props.editable indicates if content is editable
 * @param props.addTask function to add an activity task
 * @param props.editTask function to edit an activity task
 * @param props.deleteTask function to delete an activity task
 *
 */
const Activity = (props: {
    activity: InspectionActivity;
    uncolored?: boolean;
    editable?: boolean;
    addTask?: (activity: InspectionActivity, method: string) => void;
    editTask?: (activity: InspectionActivity, method: string, task: string, index: number) => void;
    deleteTask?: (activity: InspectionActivity, method: string, index: number) => void;
}) => {
    const classes = useInspectionActivityClasses();
    const { t } = useTranslation(['inspection-creation']);
    const status = { [props.activity.type]: !props.uncolored };

    return (
        <div className={clsx(classes.root, status)}>
            <span>{t(`${mod}.${props.activity.type}-inspection`)}</span>
            <MethodTasks
                {...props}
                preparation
                name={t(`${mod}.inspection-preparation`)}
                tasks={props.activity.preparation_steps}
            />
            {Object.entries(props.activity.methods)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([name, tasks]) => {
                    if (name.startsWith('\n')) return undefined;
                    return <MethodTasks key={name} {...props} name={name} tasks={tasks} />;
                })}
        </div>
    );
};

const useActivityStepsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 30,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        '& > span': {
            flex: 1,
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h5,
            marginBottom: 12,
            textTransform: 'capitalize',
        },
    },
    icon: {
        padding: 6,
        margin: '-6px 0',
        '&.internal': {
            color: theme.colors.green[4],
        },
        '&.external': {
            color: theme.colors.amber[1],
        },
    },
    divider: {
        margin: '5px 0',
        backgroundColor: theme.colors.green[4],
        '&.internal': {
            backgroundColor: theme.colors.green[4],
        },
        '&.external': {
            backgroundColor: theme.colors.amber[1],
        },
    },
    tasks: {
        margin: '-5px 0',
        '& > div': {
            margin: '5px 0',
        },
    },
});

/**
 * Component to list and edit method tasks.
 *
 * @param props.preparation indicates if activity is preparation tasks
 * @param props.name name of the method
 * @param props.tasks tasks of the method
 * @param props.activity activity that contains the method
 * @param props.addTask function to add an activity task
 * @param props.editTask function to edit an activity task
 * @param props.deleteTask function to delete an activity task
 */
const MethodTasks = (props: {
    preparation?: boolean;
    name: string;
    tasks: string[];
    activity: InspectionActivity;
    editable?: boolean;
    addTask?: (activity: InspectionActivity, method: string) => void;
    editTask?: (activity: InspectionActivity, method: string, task: string, index: number) => void;
    deleteTask?: (activity: InspectionActivity, method: string, index: number) => void;
}) => {
    const classes = useActivityStepsClasses();
    const activity = !props.preparation ? props.name : '';
    const status = { [props.activity.type]: true };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span>{props.name}</span>
                {props.editable && (
                    <IconButton
                        className={clsx(classes.icon, status)}
                        size='small'
                        onClick={() => props.addTask?.(props.activity, activity)}
                    >
                        <PlusCircle />
                    </IconButton>
                )}
            </div>
            <Divider className={clsx(classes.divider, status)} />
            <div className={classes.tasks}>
                {props.tasks.map((step, i) => (
                    <CheckInput
                        key={i}
                        defaultValue={step}
                        editable={props.editable}
                        onConfirm={value => props.editTask?.(props.activity, activity, value, i)}
                        onDelete={() => props.deleteTask?.(props.activity, activity, i)}
                    />
                ))}
            </div>
        </div>
    );
};
